import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  XMarkIcon,
  ClipboardDocumentListIcon,
  BuildingOfficeIcon,
  MoonIcon,
  SunIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  LockClosedIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { errText } from '../../services/scheduleService';
import {
  BLOCK_TYPES,
  PRESETS,
  TYPE_ORDER,
  doctorName,
  draftConflict,
  fmtDayName,
  fmtDayNum,
  initials,
  startOfWeek,
  toMin,
  weekDays,
} from './scheduleUtils';
import SessionPatients from './SessionPatients';
import SurgeonPicker from '../../components/surgeons/SurgeonPicker';

const TYPE_ICONS = { theatre: ClipboardDocumentListIcon, rooms: BuildingOfficeIcon, on_call: MoonIcon, leave: SunIcon };
const inputCls =
  'w-full rounded-lg border border-slate-300 p-2.5 text-sm outline-none transition-shadow focus:border-brand-navy focus:ring-2 focus:ring-brand-teal/40 disabled:bg-slate-50 disabled:text-slate-500';

/**
 * Slide-over editor for one session. Designed to be filled in a few clicks:
 * type → preset time → hospital (recent ones are one tap) → save. "Repeat"
 * drops the same session onto other days of that week in one go.
 */
const BlockEditor = ({ initial, isNew, doctors, hospitals, blocks, colours, canEdit, onClose, onSave, onDelete, onAddHospital, canBill, onBill, onQuoteChanged, onQuoteAssistant }) => {
  const [draft, setDraft] = useState(initial);
  const [extraDates, setExtraDates] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const editable = canEdit(initial.buid);
  const editableDoctors = doctors.filter((d) => canEdit(d.uid));
  const type = BLOCK_TYPES[draft.btype];
  const colour = colours[draft.buid] || '#1B4D8C';
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const repeatWeek = useMemo(() => weekDays(startOfWeek(draft.bdate)), [draft.bdate]);
  const dates = useMemo(
    () => [draft.bdate, ...[...extraDates].filter((d) => repeatWeek.includes(d) && d !== draft.bdate)].sort(),
    [draft.bdate, extraDates, repeatWeek]
  );
  const warning = draftConflict(draft, dates, blocks);

  // This doctor's most-used hospitals, for one-tap picking.
  const recent = useMemo(() => {
    const counts = {};
    blocks.forEach((b) => {
      if (b.buid === draft.buid && b.bhid) counts[b.bhid] = (counts[b.bhid] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([hid]) => hospitals.find((h) => h.hid === parseInt(hid, 10)))
      .filter((h) => h && h.his_active)
      .slice(0, 4);
  }, [blocks, draft.buid, hospitals]);

  const changeType = (t) =>
    setDraft((d) => {
      if (t === d.btype) return d;
      if (t === 'leave') return { ...d, btype: t, bstart: '', bend: '' };
      const keepTimes = BLOCK_TYPES[t].timed && d.btype !== 'on_call' && d.bstart && d.bend && d.bstart < d.bend;
      if (keepTimes) return { ...d, btype: t };
      const p = PRESETS[t][0];
      return { ...d, btype: t, bstart: p.start, bend: p.end };
    });

  const toggleRepeat = (day) =>
    setExtraDates((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day); else next.add(day);
      return next;
    });

  const save = async () => {
    setErr('');
    if (type.timed) {
      if (!draft.bstart || !draft.bend) return setErr('Pick a start and end time.');
      if (draft.bstart >= draft.bend) return setErr('The session must end after it starts.');
    }
    if (draft.btype === 'on_call' && !!draft.bstart !== !!draft.bend) return setErr('Give both on-call times, or neither.');
    setSaving(true);
    try {
      await onSave(draft, dates);
    } catch (e) {
      setErr(errText(e));
      setSaving(false);
    }
    return undefined;
  };

  const remove = async () => {
    setSaving(true);
    try {
      await onDelete(draft.bid);
    } catch (e) {
      setErr(errText(e));
      setSaving(false);
    }
  };

  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && editable) saveRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, editable]);

  const overnight = draft.btype === 'on_call' && draft.bstart && draft.bend && toMin(draft.bend) <= toMin(draft.bstart);
  const doctor = doctors.find((d) => d.uid === draft.buid);
  const title = !editable ? 'Session details' : isNew ? 'New session' : 'Edit session';
  const saveLabel = saving ? 'Saving…' : isNew ? (dates.length > 1 ? `Add ${dates.length} sessions` : 'Add session') : 'Save changes';

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-brand-deep/30" onMouseDown={onClose}>
      <div className="flex h-full w-full max-w-md animate-slide-in flex-col bg-white shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="h-1.5 shrink-0" style={{ background: colour }} />
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="text-lg font-bold text-brand-deep">{title}</h2>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100" title="Close (Esc)">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <fieldset disabled={!editable || saving} className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-5 px-5 py-5">
            {!editable && (
              <div className="flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2.5 text-sm text-slate-500">
                <LockClosedIcon className="mt-0.5 h-4 w-4 shrink-0" />
                Only {doctorName(doctor)}, partners with edit rights, or a practice administrator can change this session.
              </div>
            )}

            {/* Doctor */}
            <Section label="Doctor">
              {editable && editableDoctors.length > 1 ? (
                <select className={inputCls} value={draft.buid} onChange={(e) => set('buid', parseInt(e.target.value, 10))}>
                  {editableDoctors.map((d) => (
                    <option key={d.uid} value={d.uid}>{doctorName(d)}</option>
                  ))}
                </select>
              ) : (
                <div className="flex items-center gap-2.5">
                  <span className="grid h-8 w-8 place-items-center rounded-full text-[11px] font-bold text-white" style={{ background: colour }}>
                    {initials(doctor)}
                  </span>
                  <span className="text-sm font-semibold text-slate-700">{doctorName(doctor)}</span>
                </div>
              )}
            </Section>

            {/* Type */}
            <Section label="What">
              <div className="grid grid-cols-4 gap-2">
                {TYPE_ORDER.map((t) => {
                  const Icon = TYPE_ICONS[t];
                  const on = draft.btype === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => changeType(t)}
                      className={`flex flex-col items-center gap-1 rounded-lg border px-1 py-2.5 text-xs font-semibold transition ${
                        on ? 'border-brand-navy bg-brand-navy/5 text-brand-navy' : 'border-slate-200 text-slate-500 hover:border-slate-300'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      {BLOCK_TYPES[t].short}
                    </button>
                  );
                })}
              </div>
            </Section>

            {/* Date + repeat */}
            <Section label="When">
              <input type="date" className={inputCls} value={draft.bdate} onChange={(e) => e.target.value && set('bdate', e.target.value)} />
              {isNew && (
                <div className="mt-2.5">
                  <div className="mb-1.5 text-xs text-slate-500">Repeat on other days this week</div>
                  <div className="flex gap-1.5">
                    {repeatWeek.map((day) => {
                      const base = day === draft.bdate;
                      const on = base || extraDates.has(day);
                      return (
                        <button
                          key={day}
                          type="button"
                          disabled={base}
                          onClick={() => toggleRepeat(day)}
                          title={base ? 'The session’s own date' : on ? 'Remove this day' : 'Also add on this day'}
                          className={`flex h-11 flex-1 flex-col items-center justify-center rounded-lg border text-[10px] font-semibold leading-3 transition ${
                            on ? 'border-transparent bg-brand-navy text-white' : 'border-slate-200 text-slate-500 hover:border-brand-navy'
                          } ${base ? 'cursor-default' : ''}`}
                        >
                          {fmtDayName(day).slice(0, 2)}
                          <span className="mt-0.5 text-sm">{fmtDayNum(day)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </Section>

            {/* Times */}
            {draft.btype !== 'leave' && (
              <Section label={draft.btype === 'on_call' ? 'Hours (optional)' : 'Time'}>
                <div className="mb-2.5 flex flex-wrap gap-1.5">
                  {PRESETS[draft.btype].map((p) => {
                    const on = draft.bstart === p.start && draft.bend === p.end;
                    return (
                      <button
                        key={p.label}
                        type="button"
                        onClick={() => setDraft((d) => ({ ...d, bstart: p.start, bend: p.end }))}
                        className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                          on ? 'border-brand-teal bg-brand-teal/15 text-brand-deep' : 'border-slate-200 text-slate-500 hover:border-brand-teal'
                        }`}
                      >
                        {p.label} <span className="font-normal opacity-70">{p.start}–{p.end}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <input type="time" step={900} className={inputCls} value={draft.bstart} onChange={(e) => set('bstart', e.target.value)} />
                  <span className="text-sm text-slate-400">to</span>
                  <input type="time" step={900} className={inputCls} value={draft.bend} onChange={(e) => set('bend', e.target.value)} />
                </div>
                {overnight && <div className="mt-1.5 text-xs text-slate-500">Ends the next morning.</div>}
              </Section>
            )}

            {/* Hospital */}
            {draft.btype !== 'leave' && (
              <Section label="Hospital">
                <HospitalPicker value={draft.bhid} hospitals={hospitals} onChange={(hid) => set('bhid', hid)} onAdd={onAddHospital} />
                {recent.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {recent.map((h) => (
                      <button
                        key={h.hid}
                        type="button"
                        onClick={() => set('bhid', h.hid)}
                        className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold transition ${
                          draft.bhid === h.hid ? 'border-brand-navy bg-brand-navy text-white' : 'border-slate-200 text-slate-500 hover:border-brand-navy'
                        }`}
                      >
                        {h.hshort || h.hname}
                      </button>
                    ))}
                  </div>
                )}
              </Section>
            )}

            {/* Theatre details */}
            {draft.btype === 'theatre' && (
              <div className="grid gap-3 sm:grid-cols-[7rem_1fr]">
                <Section label="Theatre">
                  <input className={inputCls} value={draft.btheatre} onChange={(e) => set('btheatre', e.target.value)} placeholder="Theatre 3" />
                </Section>
                <Section label="Surgeon">
                  <SurgeonPicker
                    compact
                    disabled={!editable}
                    value={draft.bsurgeon_id ? { surgeon_id: draft.bsurgeon_id, display_name: draft.bsurgeon, practice_number: draft.bsurgeon_practice_number } : null}
                    fallbackName={draft.bsurgeon}
                    onChange={(s) => setDraft((d) => ({ ...d, bsurgeon_id: s?.surgeon_id || null, bsurgeon: s ? s.display_name : '', bsurgeon_practice_number: s?.practice_number || null }))}
                  />
                </Section>
              </div>
            )}

            {/* Patients — a theatre list usually has several. */}
            {draft.btype !== 'leave' && (
              <Section label="Patients">
                <SessionPatients
                  patients={draft.patients}
                  onChange={(list) => set('patients', list)}
                  editable={editable}
                  canBill={!isNew && !!canBill?.(initial)}
                  onBill={(p) => onBill(initial, p)}
                  onQuoteAssistant={onQuoteAssistant ? (p) => onQuoteAssistant(initial, p) : undefined}
                  saved={new Set((initial.patients || []).map((p) => p.patient_id))}
                  booking={initial}
                  hospitals={hospitals}
                  onQuoteChanged={onQuoteChanged}
                />
              </Section>
            )}

            <Section label="Notes">
              <textarea
                rows={2}
                className={`${inputCls} resize-none`}
                value={draft.bnotes}
                onChange={(e) => set('bnotes', e.target.value)}
                placeholder={draft.btype === 'leave' ? 'Conference, holiday…' : 'Anything the practice should know'}
              />
            </Section>

            {warning && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                <span><b>Heads up:</b> {warning} You can still save.</span>
              </div>
            )}
            {err && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">{err}</div>}
          </div>
        </fieldset>

        {editable && (
          <div className="flex items-center gap-2 border-t border-slate-100 px-5 py-4">
            {!isNew && (confirmDelete ? (
              <button onClick={remove} disabled={saving} className="rounded-lg bg-red-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-red-700">
                Yes, delete
              </button>
            ) : (
              <button onClick={() => setConfirmDelete(true)} className="rounded-lg p-2.5 text-slate-400 hover:bg-red-50 hover:text-red-600" title="Delete session">
                <TrashIcon className="h-5 w-5" />
              </button>
            ))}
            <div className="flex-1" />
            <button onClick={onClose} className="rounded-lg px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
            <button
              onClick={save}
              disabled={saving}
              title="Save (Ctrl+Enter)"
              className="rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-60"
            >
              {saveLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Section = ({ label, children }) => (
  <div>
    <div className="mb-1.5 text-[0.8rem] font-semibold text-slate-600">{label}</div>
    {children}
  </div>
);

// Type-to-search hospital box; offers to add the typed name if it isn't listed.
const HospitalPicker = ({ value, hospitals, onChange, onAdd }) => {
  const selected = hospitals.find((h) => h.hid === value);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const q = query.trim().toLowerCase();
  const matches = hospitals.filter(
    (h) => h.his_active && (!q || h.hname.toLowerCase().includes(q) || (h.hshort || '').toLowerCase().includes(q))
  );
  const exact = hospitals.some((h) => h.hname.toLowerCase() === q);
  const items = [...matches.map((h) => ({ h })), ...(q && !exact ? [{ add: query.trim() }] : [])];

  const pick = async (item) => {
    setError('');
    if (item.h) {
      onChange(item.h.hid);
    } else {
      setBusy(true);
      try {
        const h = await onAdd(item.add);
        onChange(h.hid);
      } catch (e) {
        setError(errText(e));
      } finally {
        setBusy(false);
      }
    }
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, items.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === 'Enter' && open && items[active]) { e.preventDefault(); pick(items[active]); }
    if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); }
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <input
          className={`${inputCls} pr-9`}
          value={open ? query : selected?.hname || ''}
          placeholder={busy ? 'Adding…' : 'Search or add a hospital'}
          onFocus={() => { setOpen(true); setActive(0); }}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
          onKeyDown={onKeyDown}
        />
        {selected && !open && (
          <button type="button" onClick={() => onChange(null)} title="Clear" className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100">
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>
      {open && items.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {items.map((item, i) => (
            <li key={item.h ? item.h.hid : 'add'}>
              <button
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(item)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${i === active ? 'bg-slate-100' : ''}`}
              >
                {item.h ? (
                  <>
                    <span className="flex-1 truncate text-slate-700">{item.h.hname}</span>
                    {item.h.hid === value && <span className="text-xs font-semibold text-brand-teal">Selected</span>}
                  </>
                ) : (
                  <span className="flex items-center gap-1.5 font-semibold text-brand-navy">
                    <PlusIcon className="h-4 w-4" /> Add “{item.add}”
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <div className="mt-1 text-xs text-red-600">{error}</div>}
    </div>
  );
};

export default BlockEditor;
